import React from "react";
import VideoBlogLayout from "../../components/VideoBlogLayout";

const BlogEndToEndPatientJourney = () => (
  <VideoBlogLayout
    category="Video Demo"
    eyebrow="End-to-End Patient Journey"
    title="What it looks like when AI runs every step of the patient journey"
    subtitle="A full walkthrough — phone call to recall — of how the Asha AI Operating System handles scheduling, intake, verification, check-in, clinical, claims, EOBs, billing, and denials in one unified system."
    author="Asha Team"
    date="April 23, 2026"
    readTime="17 min watch"
    videoUrl="https://www.loom.com/embed/4a10b9b1620043e48006b0a587356068"
    videoTitle="How the Asha AI Operating System Runs the Patient Journey End-to-End"
    videoProvider="loom"
  >
    <div className="space-y-14">
      <div>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          Most dental software gives you ten tools and tells your staff to go
          use them. Asha gives you ten softwares combined into one system, plus
          20+ AI employees that perform the work your staff would otherwise do
          — from the first phone call all the way through to recall.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          This walkthrough follows a single patient through the full journey so
          you can see how every AI agent hands off to the next, and what your
          team is actually left to review.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          1. The patient calls. AI picks up.
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          The moment a patient dials the office, AI answers and talks to them
          intelligently until they're scheduled. It respects your full set of
          time-block and operatory-based scheduling rules — down to the kind of
          specificity like{" "}
          <em>
            "every other Tuesday from 2 to 5 PM, only these providers and these
            services in this operatory."
          </em>{" "}
          Add the rule once and every AI agent instantly knows how to schedule
          against it.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          You choose the voice. You choose how much it reasons vs. how fast it
          responds. You train it in plain English on how to handle objections,
          which questions to ask, and what actions to take after the call —
          like <em>"every billing call, create a task for Stephanie."</em>
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          2. Intake — including the insurance card photo
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          When the call ends, insurance information is dropped into the
          patient's overview section automatically. If anything is missing or
          invalid, the AI Patient Intake Agent reaches out to the patient
          directly to fill out whatever intake form you've configured in the
          Forms tab.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          If your form has an insurance-card upload field, the patient just
          snaps a photo. A multimodal vision model reads the card and writes
          the subscriber ID and remaining fields straight into the chart — no
          staff chasing, no transcription errors.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          3. Insurance verification, in three tiers
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          Nearly every office we talk to has tried verification tools like
          Zuub, Vyne Trellis (formerly known as airPay), or Overjet and found
          the breakdowns too thin. We get richer benefits data by stacking
          three layers of agents on top of each other:
        </p>
        <ul className="space-y-3 text-lg text-gray-600 leading-relaxed">
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                Multiple clearing houses,
              </strong>{" "}
              not just one — so when a single source returns a partial
              breakdown, we piece in the rest from the others.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                AI-generated benefits summary
              </strong>{" "}
              tied to the procedure codes the patient is scheduled for, with
              anything your staff needs to pay attention to highlighted at the
              top.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                Laura — the AI Insurance Caller —
              </strong>{" "}
              tags in when online breakdowns are missing procedure histories,
              special limitations, or treatment-specific detail. She calls,
              waits on hold, and speaks to a representative to fill the gap.
            </span>
          </li>
        </ul>
        <p className="text-lg text-gray-600 leading-relaxed mt-4">
          Every call Laura makes on your behalf is visible on her AI staff
          screen in the portal, so nothing is a black box.
        </p>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-2">
          The point isn't that any one tier is perfect
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          Every part of the benefits-gathering process has gaps. The Asha
          design is to layer multiple AI agents specifically to overcome those
          gaps — which is why offices tell us our breakdowns are far better
          than tools they pay hundreds of dollars a month for today.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          4. Confirmations and schedule optimization
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          With Asha, your staff stops chasing patients. If a patient doesn't
          respond to SMS, AI calls to confirm. If they can't make the original
          time, AI reschedules them directly into your schedule — still
          following your rules — and then calls other patients to fill the
          opened slot.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          Those fill candidates come from your ASAP/wait list or from custom
          context you write in plain English. Across the day, the AI Schedule
          Optimizer takes a patchy schedule and tightens it by performing
          dozens of intelligent actions per appointment.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          5. The check-in flow
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          When the patient arrives, the PMS walks your team through check-in.
          You can update anything that changed, add a secondary insurance and
          re-run eligibility on the spot (seconds), and collect payment via
          either a Stripe payment link or the physical terminal we ship to
          you. Card swipes on the terminal auto-reconcile into the patient's
          ledger.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          Any required forms surface right in the flow — you can send them to
          the patient with one click. Final clinical notes for your clinical
          team go in at the bottom, and check-in is done.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          6. Clinical: charting, scribing, perio
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          Pull up the odontogram and start a session (for audit-trail
          compliance). Keyboard shortcuts let you search procedures without
          reaching for a mouse. Procedures show up in the tooth detail on the
          right. You can resize the odontogram, switch layers, and change
          dentition.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          The AI Charting Assistant documents every update to the clinical
          ledger automatically. Hit{" "}
          <strong className="text-gray-900 font-semibold">Start Scribing</strong>{" "}
          and it listens — the raw transcript appears on the right, then when
          you end the session it rewrites the transcript into clinical notes
          on the left, in the template you configured.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          For perio, an assistant can type pocket depths or you can voice-chart
          them. Add screenings inline. Sign the note when you're done and the
          system locks it.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          7. Treatment plan + auto pre-auth
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          Generate the treatment plan with AI and it'll read the patient's full
          clinical history plus everything that happened in this session to
          write a personalized narrative explaining why now is the right time
          to pursue treatment. Newer versions let you translate the plan into
          another language with one click.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          The moment the plan is created, AI fires off a pre-auth automatically
          and shows a patient-responsibility estimate based on current
          benefits. When adjudication comes back, the estimate updates itself.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          8. Claims, prepared and submitted
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          Claims are scheduled to auto-submit the day after the visit so your
          staff has time to finalize codes and notes. When the AI prepares a
          claim, it doesn't follow a rule list — it dynamically reasons over
          the payer, the procedure codes, the patient's clinical data, prior
          claim history, and known denial patterns for these procedures, then
          corrects its own work and only fires when fully confident.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          It fills the information, writes the narrative, and auto-attaches
          imaging pulled from your sensor.
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          What "autonomy %" actually means
        </h3>
        <p className="text-base text-gray-700 leading-relaxed mb-3">
          For most offices, claims, EOBs, and pre-auths run at{" "}
          <strong className="text-gray-900 font-semibold">65–75%</strong>{" "}
          autonomy — the AI is confident enough to submit on its own. The
          remaining 25–35%, it still does the full prep (codes, attachments,
          narrative) and flags it for your team to glance over.
        </p>
        <p className="text-base text-gray-700 leading-relaxed">
          For denials, autonomy is lower —{" "}
          <strong className="text-gray-900 font-semibold">~25–30%</strong> —
          because we want staff to review the appeal letter even when AI is
          confident in it.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          9. EOB posting — every input path covered
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          On day one, we ERA-enroll you with dozens of payers so EOBs come back
          electronically. For payers that don't support ERA:
        </p>
        <ul className="space-y-3 text-lg text-gray-600 leading-relaxed">
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">Fax EOBs</strong>{" "}
              — AI reads inbound faxes, files them to the patient chart, and
              hands them off to the EOB posting agent.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">Paper EOBs</strong>{" "}
              — staff drag-and-drop in bulk and AI posts them too.
            </span>
          </li>
        </ul>
        <p className="text-lg text-gray-600 leading-relaxed mt-4">
          During onboarding, your Account Manager captures which payers you're
          in-network with — at the practice and the provider level — so when
          AI calculates adjustments, it uses the right contracted rates. Then,
          before posting, AI reads your bank transactions via Plaid and
          reconciles the actual payment — even when payer transaction
          descriptions are hard to decipher — before updating the patient's
          ledger.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          10. Patient statements and AI patient billing
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed">
          Once we know what insurance paid, statements get prepped
          automatically and the AI Patient Billing Agent calls and texts
          patients until they pay. Stripe powers secure SMS payment links;
          paid statements reconcile straight into the ledger. You can
          configure payment plans and tell the agent — in plain English — how
          to offer them, how often to reach out, and at which day-marks (e.g.,{" "}
          <em>
            "reach out up to 3 times within 30 days, then again at the 60, 90,
            and 120-day marks"
          </em>
          ).
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          11. Fighting denials, in loops
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          When an ERA comes back denied, AI doesn't reason once and stop. It
          loops — checking pre-auth history, installment history, ledger math,
          prior clinical context — until it identifies the root cause. A
          devil's-advocate agent challenges its conclusions before it commits
          to a path.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          When it lands, AI drafts the appeal letter, resubmits the claim, or
          fires off the secondary submission — whichever is right.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          12. Hygiene recall and case acceptance — months later
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          The same recipe powers proactive outbound. Before reaching out, the
          recall agent builds context on the patient — clinical history, tooth
          chart, perio data, screenings, benefits, past and upcoming
          appointments, claims, and prior communications with any staff or AI
          agent.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          Because we also run your phones, fax, and patient messaging, that
          context is genuinely complete — not stitched together from five
          disconnected vendors.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          And the agents maintain memory across months. If a patient says{" "}
          <em>"I'm on vacation, call me in three weeks,"</em> the AI calls
          back in three weeks and opens with{" "}
          <em>"how was the trip?"</em> — the kind of nuance that lets these
          agents actually build relationships and convert, instead of becoming
          background noise. Case acceptance follow-ups for unaccepted plans
          run on the same model.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          13. Ask Asha anything
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed">
          When you want to ask{" "}
          <em>"analyze my last three months of claims, show me where I'm losing money, and write a plan for my team,"</em>{" "}
          you can type — or speak, via built-in voice dictation. The copilot
          pulls prior claims and EOBs, double-checks itself, reasons again,
          and produces a report with citations.
        </p>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-2">
          10 softwares + 20+ AI employees, in one system
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          We don't hand you ten softwares and tell your staff to go run them.
          We hand you ten softwares combined into one system —{" "}
          <strong className="text-gray-900 font-semibold">
            plus the AI employees to do the work.
          </strong>{" "}
          Running a practice shifts from constantly hiring, retaining, and
          backfilling staff who are always behind, to running most of your
          office on autopilot and only intervening when an AI agent flags
          something for review.
        </p>
      </div>
    </div>
  </VideoBlogLayout>
);

export default BlogEndToEndPatientJourney;
